// Assistência → "Nova assistência" (pedido 2026-10-16): nome do cliente →
// tipo de serviço → veículo (mostra se ele tem assistência pelo plano, aba
// Clientes) → se for guincho, o Ponto B (para onde o veículo vai). Ao abrir,
// já segue para escolher/acionar os guinchos (AssistanceDispatchPanel).
import { useMemo, useState } from "react";
import { APIProvider, AdvancedMarker, Map as GoogleMap } from "@vis.gl/react-google-maps";
import { AlertTriangle, Check, ChevronsUpDown, Crosshair, Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { GOOGLE_MAPS_CLEAN_STYLE, hasCustomGoogleMapId, resolveGoogleMapId } from "@/lib/google-maps-style";
import {
  useAssistanceClientSummary,
  useCreateAssistanceRequest,
  useDevicePermissions,
  useLinkableUsers,
  useServiceGroups,
  useServiceTypes,
  useServiceUsage,
  useVehicleMetadataSummaries,
} from "@/hooks/queries";
import { useAssistanceVehiclePlans } from "@/hooks/queries/assistance-center";
import { gpsDriveAssistanceCenterService } from "@/services/gpsdrive-assistance-center.service";
import type { LinkableUser } from "@/services/gpsdrive-users.service";

export const isTowService = (name: string) => /guinch|reboqu/i.test(name);
const field = "h-10 w-full rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const BILLING_LABEL: Record<string, { text: string; tone: string }> = {
  em_dia: { text: "Em dia", tone: "text-[var(--asst-done)]" },
  atraso: { text: "Com fatura em atraso", tone: "text-destructive" },
  sem_pendencias: { text: "Sem pendências", tone: "text-[var(--asst-done)]" },
  nao_sincronizado: { text: "Sem cobrança sincronizada", tone: "text-muted-foreground" },
};

export function AssistanceNewRequestDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (requestId: string, isTow: boolean) => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="asst-root max-h-[92vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova assistência</DialogTitle>
        </DialogHeader>
        <AssistanceNewRequestForm onCreated={onCreated} onCancel={onClose} />
      </DialogContent>
    </Dialog>
  );
}

// Mesmo formulário na web (janela acima) e no celular (Perfil → Registrar
// Assistência, mobile-app.tsx) — pedido 2026-10-16: "no celular deve ser
// igual também".
export function AssistanceNewRequestForm({
  onCreated,
  onCancel,
}: {
  onCreated: (requestId: string, isTow: boolean) => void;
  onCancel?: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [client, setClient] = useState<LinkableUser | null>(null);
  const [serviceTypeId, setServiceTypeId] = useState("");
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [destination, setDestination] = useState("");
  const [destCoords, setDestCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateMsg, setLocateMsg] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: linkable = [], isLoading: loadingUsers } = useLinkableUsers();
  const clients = useMemo(() => linkable.filter((u) => u.status === "minha_empresa"), [linkable]);
  const { data: summary } = useAssistanceClientSummary(client?.id);
  const { data: devices = [], isLoading: loadingDevices } = useDevicePermissions(client?.id);
  const { data: serviceTypes = [] } = useServiceTypes();
  const { data: groups = [] } = useServiceGroups();
  const { data: vehiclePlans } = useAssistanceVehiclePlans();
  const { data: usage } = useServiceUsage(client?.id, deviceId ?? undefined, serviceTypeId || undefined);
  const create = useCreateAssistanceRequest();
  // Reserva: veículo com Plano "com assistência" no cadastro (quem não vê a
  // lista da aba Clientes, ou veículo fora da planilha).
  const deviceIds = useMemo(() => devices.map((d) => String(d.id)), [devices]);
  const { data: metaSummaries } = useVehicleMetadataSummaries(deviceIds, !!client);

  const active = serviceTypes.filter((s) => s.active);
  const service = active.find((s) => s.id === serviceTypeId);
  const tow = !!service && isTowService(service.name);

  // Plano com assistência por veículo (planilha, plano do veículo ou contrato).
  const planByDevice = useMemo(() => {
    const m = new Map<number, string>();
    for (const d of devices) {
      const hit = vehiclePlans?.[String(d.id)];
      if (hit) m.set(d.id, hit.groupName ? `${hit.planName} · ${hit.groupName}` : hit.planName);
      else if (metaSummaries?.[String(d.id)]?.hasAssistancePlan) m.set(d.id, "Com assistência");
    }
    return m;
  }, [devices, vehiclePlans, metaSummaries]);
  const clientPlans = [...planByDevice.keys()];

  const locate = async () => {
    if (!destination.trim()) return setLocateMsg("Digite o endereço do destino");
    setLocating(true);
    setLocateMsg(null);
    try {
      const { location } = await gpsDriveAssistanceCenterService.geocodeText(destination);
      if (location) setDestCoords(location);
      else setLocateMsg("Endereço não encontrado — tente com cidade, ou clique no mapa depois de localizar algo próximo.");
    } catch (e) {
      setLocateMsg(e instanceof GpsDriveApiError ? e.message : "Não foi possível localizar agora");
    } finally {
      setLocating(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (!client) return setError("Escolha o cliente");
    if (!serviceTypeId) return setError("Escolha o tipo de serviço");
    if (!deviceId) return setError("Escolha o veículo");
    if (tow && !destination.trim()) return setError("Digite o Ponto B (para onde o veículo vai)");
    if (usage && !usage.canRequest) return setError("Este serviço ainda não está liberado para este veículo (limite do plano).");
    let coords = destCoords;
    if (tow && !coords) {
      coords = await gpsDriveAssistanceCenterService
        .geocodeText(destination)
        .then((r) => r.location)
        .catch(() => null);
    }
    try {
      const created = await create.mutateAsync({
        traccarUserId: client.id,
        deviceId,
        serviceTypeId,
        reason: reason.trim() || null,
        destinationAddress: tow ? destination.trim() : null,
        destinationLat: tow ? (coords?.lat ?? null) : null,
        destinationLng: tow ? (coords?.lng ?? null) : null,
      });
      onCreated(created.id, tow);
    } catch (e) {
      setError(e instanceof GpsDriveApiError ? e.message : "Não foi possível abrir a assistência");
    }
  };

  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";

  return (
    <>
        <div className="space-y-4">
          {/* 1. Cliente */}
          <div className="space-y-1">
            <span className="text-xs font-semibold">1. Cliente</span>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button type="button" className={cn(field, "flex items-center justify-between text-left")}>
                  <span className={client ? "" : "text-muted-foreground"}>{client?.name ?? (loadingUsers ? "Carregando clientes…" : "Digite o nome do cliente…")}</span>
                  <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command>
                  <CommandInput placeholder="Nome ou e-mail…" />
                  <CommandList>
                    <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                    <CommandGroup>
                      {clients.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.name} ${c.email}`}
                          onSelect={() => {
                            setClient(c);
                            setDeviceId(null);
                            setPickerOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", client?.id === c.id ? "opacity-100" : "opacity-0")} />
                          <span className="truncate">{c.name}</span>
                          <span className="ml-auto truncate pl-2 text-xs text-muted-foreground">{c.email}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {client && summary && (
              <div className="flex flex-wrap items-center gap-x-3 text-xs">
                <span className={BILLING_LABEL[summary.billing.status]?.tone}>💳 {BILLING_LABEL[summary.billing.status]?.text}</span>
                {clientPlans.length > 0 ? (
                  <span className="font-semibold text-[var(--asst-done)]">🛡 {clientPlans.length} veículo(s) com assistência</span>
                ) : (
                  <span className="text-amber-700 dark:text-amber-300">Nenhum veículo com assistência na lista</span>
                )}
              </div>
            )}
          </div>

          {/* 2. Serviço */}
          <label className="block space-y-1">
            <span className="text-xs font-semibold">2. Tipo de serviço</span>
            <select value={serviceTypeId} onChange={(e) => setServiceTypeId(e.target.value)} className={field}>
              <option value="">Escolha…</option>
              {active
                .filter((s) => !s.groupId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              {groups.map((g) => {
                const items = active.filter((s) => s.groupId === g.id);
                return items.length ? (
                  <optgroup key={g.id} label={g.name}>
                    {items.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null;
              })}
            </select>
          </label>

          {/* 3. Veículo */}
          <div className="space-y-1">
            <span className="text-xs font-semibold">3. Veículo</span>
            {!client ? (
              <p className="text-xs text-muted-foreground">Escolha o cliente primeiro.</p>
            ) : loadingDevices ? (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Carregando veículos…
              </p>
            ) : devices.length === 0 ? (
              <p className="text-xs text-muted-foreground">Este cliente não tem veículo vinculado.</p>
            ) : (
              <div className="grid gap-1.5">
                {devices.map((d) => {
                  const plan = planByDevice.get(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setDeviceId(d.id)}
                      className={cn(
                        "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                        deviceId === d.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50",
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{d.name}</span>
                      {plan ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--asst-done)_15%,transparent)] px-2 py-0.5 text-[11px] font-bold text-[var(--asst-done)]">
                          <ShieldCheck className="h-3 w-3" /> {plan}
                        </span>
                      ) : (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                          <ShieldOff className="h-3 w-3" /> Sem assistência
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {usage && (
              <p className={cn("text-xs", usage.canRequest ? "text-muted-foreground" : "font-semibold text-destructive")}>
                {usage.usageCount} de {usage.annualLimit} utilizações deste serviço no ano
                {!usage.canRequest && usage.nextAvailableAt && ` — liberado de novo em ${new Date(usage.nextAvailableAt).toLocaleDateString("pt-BR")}`}
              </p>
            )}
          </div>

          {/* 4. Ponto B */}
          {tow && (
            <div className="space-y-2 rounded-xl border p-3">
              <span className="text-xs font-semibold">4. Ponto B — para onde o veículo vai</span>
              <div className="flex gap-2">
                <input
                  value={destination}
                  onChange={(e) => {
                    setDestination(e.target.value);
                    setDestCoords(null);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && void locate()}
                  placeholder="Ex.: Oficina do João, Av. Brasil 500, Canoas"
                  className={field}
                />
                <Button type="button" variant="outline" onClick={locate} disabled={locating} className="h-10 shrink-0">
                  {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
                  Localizar
                </Button>
              </div>
              {locateMsg && <p className="text-xs text-amber-700 dark:text-amber-300">{locateMsg}</p>}
              {destCoords && apiKey && (
                <>
                  <div className="h-48 overflow-hidden rounded-lg border">
                    <APIProvider apiKey={apiKey}>
                      <GoogleMap
                        key={`${destCoords.lat.toFixed(3)}:${destCoords.lng.toFixed(3)}`}
                        mapId={resolveGoogleMapId()}
                        styles={hasCustomGoogleMapId() ? undefined : GOOGLE_MAPS_CLEAN_STYLE}
                        defaultCenter={destCoords}
                        defaultZoom={15}
                        disableDefaultUI
                        zoomControl
                        clickableIcons={false}
                        gestureHandling="cooperative"
                        onClick={(e) => e.detail.latLng && setDestCoords(e.detail.latLng)}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <AdvancedMarker
                          position={destCoords}
                          draggable
                          onDragEnd={(e) => e.latLng && setDestCoords({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
                        >
                          <div className="grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-[var(--asst-c4)] text-sm font-bold text-white shadow-lg">B</div>
                        </AdvancedMarker>
                      </GoogleMap>
                    </APIProvider>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Arraste o “B” ou clique no mapa para ajustar o ponto exato.</p>
                </>
              )}
            </div>
          )}

          <label className="block space-y-1">
            <span className="text-xs font-semibold">Observação (vai na mensagem)</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Ex.: carro não liga, está na garagem do prédio" className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </label>

          {error && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" /> {error}
            </p>
          )}
        </div>
        <DialogFooter className="mt-4 gap-2">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          )}
          <Button onClick={submit} disabled={create.isPending || (usage != null && !usage.canRequest)} className="h-11 sm:h-10">
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {tow ? "Abrir e escolher guincho" : "Registrar assistência"}
          </Button>
        </DialogFooter>
    </>
  );
}
